import { useParams, Navigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import MarketingLayout from "@/components/marketing/MarketingLayout"
import { useLegalDoc } from "@/lib/legal"
import { useBrand } from "@/hooks/use-brand"
import { LOCALE_CONFIG } from "@/config/locale-domains"

const DOC_MAP = {
  privacy: { key: "privacy", seoKey: "legalPrivacy" },
  terms: { key: "terms", seoKey: "legalTerms" },
  gdpr: { key: "gdpr", seoKey: "legalGdpr" },
}

export default function LegalPage() {
  const { doc } = useParams()
  const { t } = useTranslation()
  const brand = useBrand()
  const config = DOC_MAP[doc]
  const legal = useLegalDoc(config?.key)

  if (!config) return <Navigate to="/404" replace />

  const locale = brand.locale || "ro"
  const domain = LOCALE_CONFIG[locale]?.domain || LOCALE_CONFIG.ro.domain

  if (!legal) {
    return (
      <MarketingLayout seoKey={config.seoKey}>
        <div className="pt-36 pb-24 px-6 text-center text-muted-foreground">{t("common.loading")}</div>
      </MarketingLayout>
    )
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: legal.title,
    description: legal.description,
    dateModified: legal.updatedAt,
    url: `https://${domain}/legal/${doc}`,
  }

  return (
    <MarketingLayout
      seoKey={config.seoKey}
      title={legal.title}
      description={legal.description}
      jsonLd={jsonLd}
    >
      <article className="pt-36 pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-5">
            {t("footer.legal")}
          </p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-medium">{legal.title}</h1>
          <p className="mt-4 text-sm text-muted-foreground">
            {t("legal.lastUpdated", { date: legal.updatedAt })}
          </p>
          <p className="mt-2 text-xs text-muted-foreground italic">{t("legal.disclaimer")}</p>
          <div className="prose prose-neutral dark:prose-invert max-w-none mt-10 legal-content [&_h2]:font-display [&_h2]:text-foreground [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:text-muted-foreground [&_li]:text-muted-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{legal.body}</ReactMarkdown>
          </div>
        </div>
      </article>
    </MarketingLayout>
  )
}
